import { useMemo } from "react";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Circle,
  MessageSquareText,
  PlayCircle,
  TerminalSquare,
  UserRound,
  XCircle,
} from "lucide-react";

import { Badge } from "@workanywhere/ui";
import { renderMarkdown } from "@workanywhere/ui";
import { cn } from "@workanywhere/ui";
import {
  formatEventTitle,
  formatTime,
  getEventPreview,
  getEventTone,
  type AgentTimelineEntry,
  type PromptTimelineSource,
} from "../../agent-updates";
import { isAgentItemUpdateEvent, type AgentUpdateRecord } from "../../../../../shared/agent";

export function EventRow({
  canInspect,
  entry,
  selected,
  onSelect,
}: {
  canInspect: boolean;
  entry: AgentTimelineEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  const record = entry.kind === "event" ? entry.record : null;
  const Icon = entry.kind === "event" ? getEventIcon(entry.record) : UserRound;
  const tone = entry.kind === "event" ? getEventTone(entry.record) : "info";
  const preview = entry.kind === "event" ? getEventPreview(entry.record) : entry.prompt;
  const commandExecution = entry.kind === "event" ? getCommandExecution(entry.record) : null;
  const renderedPreview = useMemo(() => renderMarkdown(preview), [preview]);
  const canSelect = canInspect && entry.kind === "event";

  return (
    <li>
      <div
        role={canSelect ? "button" : undefined}
        tabIndex={canSelect ? 0 : undefined}
        onClick={canSelect ? onSelect : undefined}
        onKeyDown={
          canSelect
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect();
                }
              }
            : undefined
        }
        className={cn(
          "grid w-full grid-cols-[32px_minmax(0,1fr)_88px] gap-3 px-4 py-3 text-left transition-colors",
          !canSelect && "items-center",
          canSelect && "cursor-pointer hover:bg-accent/60",
          selected && "bg-accent",
        )}
      >
        <span
          className={cn(
            "flex size-7 items-center justify-center rounded-full border",
            canSelect && "mt-1",
            tone === "success" && "border-emerald-200 bg-emerald-50 text-emerald-700",
            tone === "danger" && "border-destructive/30 bg-destructive/10 text-destructive",
            tone === "info" && "border-blue-200 bg-blue-50 text-blue-700",
            tone === "neutral" && "border-border bg-muted text-muted-foreground",
          )}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0">
          {entry.kind === "prompt" ? (
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">You</span>
              <Badge variant="outline">{getPromptSourceLabel(entry.source)}</Badge>
              {entry.status ? <Badge variant="secondary">{entry.status}</Badge> : null}
            </div>
          ) : null}
          {canSelect && record ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{formatEventTitle(record)}</span>
              <Badge variant="outline">{record.event.type}</Badge>
              <Badge variant="secondary">Run {record.event.run_id}</Badge>
            </div>
          ) : null}
          {commandExecution ? (
            <div className={cn("text-sm text-muted-foreground [overflow-wrap:anywhere]", canSelect && "mt-1")}>
              <span>Executed command:</span> <span className="italic opacity-75">{commandExecution}</span>
            </div>
          ) : (
            <div
              className={cn("event-row-markdown block text-sm text-muted-foreground", canSelect && "mt-1")}
              dangerouslySetInnerHTML={{ __html: renderedPreview }}
            />
          )}
        </div>
        <span className="text-right text-xs text-muted-foreground">{formatTime(entry.receivedAt)}</span>
      </div>
    </li>
  );
}

function getPromptSourceLabel(source: PromptTimelineSource): string {
  if (source === "frontend") return "Frontend";
  if (source === "tui") return "TUI";
  return "Desktop";
}

function getEventIcon(record: AgentUpdateRecord) {
  if (record.event.type === "thread.started") return PlayCircle;
  if (record.event.type === "turn.started") return Activity;
  if (record.event.type === "turn.completed") return CheckCircle2;
  if (record.event.type === "turn.failed") return XCircle;
  if (record.event.type === "error") return AlertCircle;
  if (isAgentItemUpdateEvent(record.event) && record.event.item.type === "agent_message") return MessageSquareText;
  if (isAgentItemUpdateEvent(record.event) && record.event.item.type === "command_execution") return TerminalSquare;
  return Circle;
}

function getCommandExecution(record: AgentUpdateRecord): string | null {
  if (!isAgentItemUpdateEvent(record.event) || record.event.item.type !== "command_execution") {
    return null;
  }

  return String(record.event.item.command ?? record.event.item.id);
}
